import { Injectable } from '@angular/core';
import {ApiService, I18nHelperService} from '@xref/ngx';
import {map} from 'rxjs/operators';

export class Questionnaire {
  id: string;
  title: string;
  questions?: any[];
}

export class Question {
  id: string; // Local ID within Questionnaire
  question_id: string; // Global ID
  title_content: any;
  competency_key: string;
  competency_content: any;
  category_key: string;
  category_content: any;
  type: string;
  possible_answers: any;
}

export class QuestionnaireMetrics {
  essential: number;
  person: number;
  role: number;
}

export class CreateQuestionnaireData {
  role: string;
  industry: string;
  core_competencies: any[];
  title: string;
}

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireService {
  constructor(
    private api: ApiService,
    private i18nHelper: I18nHelperService
  ) {}

  getQuestionnaire(qId: string) {
    return this.api.get(`v1/questionnaire/${qId}`).pipe(map(data => data));
  }

  getQuestionnaireItems(qId: string) {
    return this.api.get(`v1/questionnaire/${qId}/items`).pipe(map(data => data));
  }

  getCategoryMetrics(questionnaire: Questionnaire): QuestionnaireMetrics {
    const metrics: QuestionnaireMetrics = {
      essential: 0,
      person: 0,
      role: 0
    };

    questionnaire.questions.forEach((item) => {
      switch (item.category_key) {
        case 'essential':
          metrics.essential += 1;
          break;
        case 'personal_attributes':
          metrics.person += 1;
          break;
        case 'role_specific':
          metrics.role += 1;
          break;
      }
    });

    return metrics;
  }

  createQuestionnaire(postData: CreateQuestionnaireData) {
    return this.api.post(`v1/generate`, postData).pipe(map(data => data));
  }

  makeDefaultQuestionnaireTitle(prefix: string) {
    const options = {year: 'numeric', month: 'short', day: 'numeric'};
    const dateString = new Date().toLocaleString(this.i18nHelper.locale, options as any);

    return `${prefix} ${dateString}`;
  }

  editTitle(qId: string, title: string) {
    return this.api.put(`v1/questionnaire/${qId}`, {title}).pipe(map(data => data));
  }

  reorderQuestionnaire(questionnaire: Questionnaire) {
    // API accepts list of question id's
    const questionnaireQuestions = questionnaire.questions.map(item => item.id);

    return this.api.put(`v1/questionnaire/${questionnaire.id}/order`, {
      questionnaire_questions: questionnaireQuestions
    }).pipe(map(data => data));
  }

  addQuestion(questionId: string, questionnaireId: string) {
    const postData = {
      question: questionId,
      questionnaire: questionnaireId
    };

    return this.api.post(`v1/questionnaire-item/${questionId}`, postData).pipe(map(data => data));
  }

  deleteQuestion(questionId: string) {
    return this.api.delete(`v1/questionnaire-item/${questionId}`).pipe(map(data => data));
  }

  saveQuestionnaire(qId: string, locale: string) {
    const postData = {
      language_code: locale
    };

    return this.api.post(`v1/questionnaire/${qId}/save-to-xref-database`, postData).pipe(map(data => data));
  }

  sendQuestionnaireToEmail(qId: string) {
    return this.api.post(`v1/questionnaire/${qId}/email-to-user`).pipe(map(data => data));
  }

  // User the questionnaire GET call to make use of it's implicit assigning of the user
  assignUserToQuestionnaireImplicit(qId: string) {
    return this.getQuestionnaire(qId);
  }
}
